import { Component } from '@theme/component';

const STORAGE_KEY = 'pc-language-banner';
const SESSION_KEY = 'pc-language-banner-shown';
/** The choice is remembered for a year. */
const REMEMBER_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Suggestion banner for the language of the browser (`snippets/pc-language-banner.liquid`).
 *
 * Shopify's `browsing_context_suggestions.json` answers, from the headers of the request, which language the store should
 * suggest (none when the page already is in the language of the browser). When it names one the store offers, the matching
 * offer is shown; its button submits the `localization` form that the language selectors send, so Shopify changes the language
 * and sends the visitor back to the same page. Nothing redirects by itself.
 *
 * The banner appears at most once per browser session, and not at all for a year after the visitor answers it (either button)
 * or uses a language selector. It does not move focus, closes with Escape, and only animates when motion is allowed (CSS).
 * Without JavaScript the banner stays hidden.
 *
 * Attributes of the element: `data-endpoint` is the URL of the suggestions service and `data-current-language` the code of the
 * language of the page.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [offers] - One offer per language the store can suggest; each carries `data-language`.
 *
 * @extends {Component<Refs>}
 */
class PcLanguageBanner extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {number | undefined} */
  #idle;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('keydown', this.#onKeydown, { signal });
    // Focusing or using any language menu of the page (by pointer or keyboard) is an answer too
    document.addEventListener('focusin', this.#onSelectorUse, { signal });
    document.addEventListener('change', this.#onSelectorUse, { signal });

    if (this.#isQuiet()) return;

    // Not needed for the first paint: it waits until the browser is idle
    const start = () => this.#suggest(signal);
    if ('requestIdleCallback' in window) this.#idle = window.requestIdleCallback(start, { timeout: 4000 });
    else this.#idle = window.setTimeout(start, 1500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;

    if (this.#idle !== undefined) {
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.#idle);
      else window.clearTimeout(this.#idle);
      this.#idle = undefined;
    }
  }

  /** Remembers the answer when the visitor takes the offer; the form then submits and Shopify changes the language. */
  accept() {
    this.#remember();
  }

  /** Remembers the answer and closes the banner. Called by the "stay" button. */
  dismiss() {
    this.#remember();
    this.#hide();
  }

  /**
   * Asks Shopify which language to suggest and shows the matching offer.
   * @param {AbortSignal} signal
   */
  async #suggest(signal) {
    const { endpoint, currentLanguage } = this.dataset;
    if (!endpoint || !currentLanguage) return;

    const query = new URLSearchParams({
      'language[enabled]': 'true',
      'language[exclude]': currentLanguage,
      'country[enabled]': 'false',
    });

    try {
      const response = await fetch(`${endpoint}?${query}`, { signal, headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      /** @type {{ suggestions?: Array<{ parts?: { language?: { handle?: string } } }> }} */
      const data = await response.json();
      const handle = data.suggestions?.find((suggestion) => suggestion.parts?.language?.handle)?.parts?.language?.handle;
      const offer = this.refs.offers?.find((element) => element.dataset.language === handle);
      if (!offer || signal.aborted) return;

      offer.hidden = false;
      this.hidden = false;
      this.#store(sessionStorage, SESSION_KEY, '1');
    } catch (error) {
      // A request that was cancelled or failed just means no suggestion; the page works the same
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.warn('pc-language-banner', error);
    }
  }

  /** Whether the banner must not appear: answered in the last year, or already shown in this session. */
  #isQuiet() {
    if (this.#read(sessionStorage, SESSION_KEY)) return true;

    const answered = Number(this.#read(localStorage, STORAGE_KEY));
    return Number.isFinite(answered) && answered > 0 && Date.now() - answered < REMEMBER_MS;
  }

  #remember() {
    this.#store(localStorage, STORAGE_KEY, String(Date.now()));
  }

  #hide() {
    this.hidden = true;
    for (const offer of this.refs.offers ?? []) offer.hidden = true;
  }

  /**
   * Storage can be blocked (private windows, site data off): every access is guarded and the page works without it.
   * @param {Storage} storage
   * @param {string} key
   * @returns {string | null}
   */
  #read(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   * @param {string} value
   */
  #store(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch {
      // Nothing to do: the banner may come back on the next visit
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.hidden) {
      event.stopPropagation();
      this.dismiss();
    }
  };

  /** @param {Event} event */
  #onSelectorUse = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('localization-form-component')) return;

    this.#remember();
    if (!this.hidden) this.#hide();
  };
}

if (!customElements.get('pc-language-banner')) {
  customElements.define('pc-language-banner', PcLanguageBanner);
}
